import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { eventActions } from "../../redux/eventSlice";
import { Link, useNavigate } from "react-router-dom";
import {
  Skeleton,
  Button,
  Modal,
  Pagination,
  Input,
  Form,
  message,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { NepaliDatePicker } from "nepali-datepicker-reactjs";
import "nepali-datepicker-reactjs/dist/index.css";
import { convertToEnglishDate, convertToNepaliDate } from "../../components/FormatDate";

const EventList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { events, loading } = useSelector((state) => state.events);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [isEventOpen, setIsEventOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    date: "",
    time: "",
    venue: "",
    image: null,
    remove_image: 0,
    id: null,
  });

  /* ------------------------------------------------------------------ */
  useEffect(() => {
    dispatch(eventActions.getEvents());
  }, [dispatch]);

  const handlePageChange = (page) => setCurrentPage(page);

  /* ------------------------------------------------------------------ */
  const handleShowEvent = (event) => {
    setSelectedEvent(event);
    setIsEventOpen(true);
  };

  /* ------------------------------------------------------------------ */
  const handleEdit = (event) => {
    setSelectedEvent(event);

    const imageUrl =
      event?.featured_image ||null;
console.log("Editing Event:", event);
    setEditForm({
      name: event?.name ?? "",
      description: event?.description ?? "",
      date:convertToNepaliDate( event?.date) ?? "",
      time: event?.time ?? "",
      venue: event?.venue ?? "",
      image: event.featured_image ? null : null,
      remove_image: 0,
      id: event?.file?.id ?? null,   // same as getEventById
    });

    setPreviewImage(imageUrl);
    setIsEditModalOpen(true);
  };

  /* ------------------------------------------------------------------ */
  const handleEditFormChange = (key, value) => {
    setEditForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditForm((prev) => ({
        ...prev,
        image: file,
        remove_image: 0,
      }));
      const reader = new FileReader();
      reader.onload = () => setPreviewImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setPreviewImage(null);
    setEditForm((prev) => ({
      ...prev,
      image: null,
      remove_image: 1,
    }));
  };

  /* ------------------------------------------------------------------ */
  const handleDelete = (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this event?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await dispatch(eventActions.deleteEvent(id)).unwrap();
          message.success("Event deleted successfully");
          dispatch(eventActions.getEvents());
        } catch {
          message.error("Failed to delete event");
        }
      },
    });
  };

  /* ------------------------------------------------------------------ */
  const handleUpdateEvent = async () => {
    try {
      const formData = new FormData();
      formData.append("name", editForm.name);
      formData.append("description", editForm.description);
      console.log("Converted Date:", (editForm.date));
      formData.append("date", convertToEnglishDate((editForm.date)));
      formData.append("time", editForm.time);
      formData.append("venue", editForm.venue);

      if (editForm.image) formData.append("featured_image", editForm.image);
      if (editForm.remove_image) formData.append("remove_featured_image", "1");
      if (editForm.id) formData.append("id", editForm.id);

      formData.append("_method", "PUT");

      await dispatch(
        eventActions.updateEvent({
          id: selectedEvent.id,
          eventData: formData,
        })
      ).unwrap();

      message.success("Event updated successfully");
      setIsEditModalOpen(false);
      dispatch(eventActions.getEvents());
    } catch (err) {
      message.error(err?.message || "Failed to update event");
    }
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setPreviewImage(null);
    setEditForm({
      name: "",
      description: "",
      date: "",
      time: "",
      venue: "",
      image: null,
      remove_image: 0,
      id: null,
    });
  };

  /* ------------------------------------------------------------------ */
  const paginatedEvents = events?.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
console.log("Paginated Events:", paginatedEvents);
  return (
    <div className="w-full bg-slate-200 p-6 pb-20 rounded-lg shadow-md">
      <div className="bg-white rounded-lg p-4">
        <div className="mb-4 flex justify-between">
          <h1 className="text-xl font-semibold mb-4">Event List</h1>
          <Link to="/dashboard/addevent">
            <Button
              type="default"
              className="bg-green-800 text-white hover:bg-green-600"
              icon={<PlusOutlined />}
            >
              Add Event
            </Button>
          </Link>
        </div>

       
          {loading ? (
            <div className="p-12">
              <Skeleton active />
              <Skeleton active />
              <Skeleton active />
            </div>
          ) : (
            <div className="bg-white p-4 rounded-lg overflow-x-auto">
              <table className="min-w-full border-2 border-black">
                <thead>
                  <tr className="text-left">
                    <th className="py-2 px-4 border-r border-b-2 border-black">
                      Date
                    </th>
                    <th className="py-2 px-4 border-r border-b-2 border-black">
                      Title
                    </th>
                    <th className="py-2 px-4 border-r border-b-2 border-black">
                      Image
                    </th>
                    <th className="py-2 px-4 border-b-2 border-black">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedEvents?.map((item) => (
                    <tr key={item?.id} className="hover:bg-gray-50 border-b">
                      <td className="py-2 px-4 border-r border-black">
                        {convertToNepaliDate(item?.date) || "No date available"}
                      </td>
                      <td className="py-2 px-4 border-r border-black">
                        {item?.name || "No name available"}
                      </td>
                      <td className="py-2 px-4 border-r border-black">
                        <img
                          src={
                            item.featured_image ||
                            
                            "/placeholder.png"
                          }
                          alt="event"
                          className="h-10 w-10 object-cover rounded"
                        />
                      </td>
                      <td className="py-2 px-4 flex gap-2">
                      <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(item)}   // ← full event
                        className="bg-blue-600 text-white"
                      />
                        <Button
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(item?.id)}
                        danger
                      />
                      {/* VIEW */}
                      <Button
                        icon={<EyeOutlined />}
                        onClick={() => handleShowEvent(item)}   // ← full event
                        className="bg-[#403F93] text-white"
                      />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={events?.length || 0}
            onChange={handlePageChange}
            className="mt-4 text-center"
          />
       

        {/* View Event Modal */}
        {isEventOpen && selectedEvent && (
          <Modal
            open={isEventOpen}
            onCancel={() => setIsEventOpen(false)}
            footer={null}
            width={800}
          >
            <div className="p-8">
              <h2 className="text-xl font-bold text-center mb-2">
                {selectedEvent.name}
              </h2>
              <p className="text-center">
                Date: {convertToNepaliDate(new Date(selectedEvent.date))}
              </p>
              <div className="mt-8">
                {selectedEvent.file?.original_url ? (
                  <img
                    src={selectedEvent.file.original_url}
                    alt="event"
                    className="object-cover h-80 w-80 mx-auto rounded"
                  />
                ) : (
                  <div className="h-80 w-80 mx-auto bg-gray-200 border-2 border-dashed rounded flex items-center justify-center text-gray-500">
                    No Image
                  </div>
                )}
                <p className="mt-4">
                  <strong>Description:</strong>{" "}
                  {selectedEvent.description || "No description available"}
                </p>
              </div>
            </div>
          </Modal>
        )}

        {/* Edit Event Modal */}
        {isEditModalOpen && (
          <Modal
            open={isEditModalOpen}
            onCancel={closeEditModal}
            onOk={handleUpdateEvent}
            title="Edit Event"
            okText="Update"
            cancelText="Cancel"
          >
            <Form layout="vertical">
              <Form.Item label="Event Name">
                <Input
                  placeholder="Event Name"
                  value={editForm.name}
                  onChange={(e) => handleEditFormChange("name", e.target.value)}
                />
              </Form.Item>

              <Form.Item label="Description">
                <Input.TextArea
                  placeholder="Description"
                  value={editForm.description}
                  onChange={(e) =>
                    handleEditFormChange("description", e.target.value)
                  }
                />
              </Form.Item>

              <Form.Item label="Date">
                <NepaliDatePicker
                  options={{ calenderLocale: "en", valueLocale: "en" }}
                  value={editForm.date}
                  onChange={(date) => handleEditFormChange("date", date)}
                  className="w-full custom-date-picker"
                />
              </Form.Item>

              <Form.Item label="Time">
                <Input
                  type="time"
                  value={editForm.time}
                  onChange={(e) => handleEditFormChange("time", e.target.value)}
                />
              </Form.Item>

              <Form.Item label="Venue">
                <Input
                  placeholder="Venue"
                  value={editForm.venue}
                  onChange={(e) =>
                    handleEditFormChange("venue", e.target.value)
                  }
                />
              </Form.Item>

              <Form.Item label="Event Image">
                {previewImage ? (
                  <div className="relative mb-4">
                    <img
                      src={previewImage}
                      alt="preview"
                      className="w-full h-40 object-cover rounded"
                    />
                    <Button
                      type="text"
                      danger
                      icon={<CloseOutlined />}
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 bg-white rounded-full shadow"
                    />
                  </div>
                ) : (
                  <div className="mb-4 w-full h-40 bg-gray-200 border-2 border-dashed rounded flex items-center justify-center text-gray-500">
                    No Image
                  </div>
                )}
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Form.Item>
            </Form>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default EventList;